import React, { useState, useRef } from "react";
import axios from "axios";
import { Upload, FileSpreadsheet, X, Loader2, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { API } from "../../App";
import { toast } from "sonner";

/**
 * ItemsImportModal — Import items library from CSV / XLSX (e.g. Uleadz export).
 *
 * Flow:
 *   1. User picks file (drag-drop or click).
 *   2. POST multipart to /api/invoices/library/items/import.
 *   3. Show structured summary: created / skipped (dup, empty, invalid).
 *   4. On close after a successful import, parent refreshes the items list.
 */
const ItemsImportModal = ({ open, onClose, headers, onImported, isDark }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => {
    setFile(null);
    setResult(null);
    setSubmitting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = (didImport) => {
    if (didImport && result?.created > 0) onImported?.();
    reset();
    onClose();
  };

  const validateFile = (f) => {
    if (!f) return false;
    const lower = (f.name || "").toLowerCase();
    if (!(lower.endsWith(".csv") || lower.endsWith(".xlsx") || lower.endsWith(".tsv"))) {
      toast.error("Only CSV, TSV, or XLSX files are supported");
      return false;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB)");
      return false;
    }
    return true;
  };

  const handleFile = (f) => {
    if (validateFile(f)) {
      setFile(f);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setSubmitting(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Note: do NOT manually set Content-Type. Axios + browser will add the
      // correct `multipart/form-data; boundary=...` automatically. Setting it
      // manually breaks the boundary and causes proxies to reject the request.
      const res = await axios.post(
        `${API}/invoices/library/items/import`,
        formData,
        { headers: { ...headers } }
      );
      setResult(res.data);
      if (res.data.created > 0) {
        toast.success(`Imported ${res.data.created} item${res.data.created === 1 ? "" : "s"}`);
      } else {
        toast.info("No new items were imported (all duplicates or empty)");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Import failed");
      setResult({ error: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const dropZoneCls = `border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
    dragOver
      ? "border-[#FFCC00] bg-[#FFCC00]/10"
      : isDark
      ? "border-white/10 hover:border-white/20 bg-white/[0.02]"
      : "border-gray-200 hover:border-gray-300 bg-gray-50"
  }`;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(false); }}>
      <DialogContent
        className={`sm:max-w-md ${isDark ? "bg-[#0b0d10] border-white/10" : "bg-white border-gray-200"}`}
        data-testid="items-import-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Upload className="w-4 h-4 text-[#FFCC00]" /> Import Items
          </DialogTitle>
        </DialogHeader>

        {/* Drop zone */}
        {!result && (
          <>
            <div
              className={dropZoneCls}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              data-testid="items-import-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.xlsx"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="items-import-file-input"
              />
              <FileSpreadsheet className={`w-10 h-10 mx-auto mb-2 ${isDark ? "text-white/30" : "text-gray-400"}`} />
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm font-medium text-foreground">{file.name}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                    className="text-muted-foreground hover:text-red-500"
                    data-testid="clear-file"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-foreground font-medium">
                    Drop CSV / XLSX here, or click to browse
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Supports Uleadz items export format and similar
                  </p>
                </>
              )}
            </div>

            {/* Helper text — column expectations */}
            <div className={`rounded-lg p-3 text-[11px] flex items-start gap-2 ${
              isDark ? "bg-blue-500/10 text-blue-200 border border-blue-500/20" : "bg-blue-50 text-blue-900 border border-blue-100"
            }`}>
              <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
              <div className="leading-relaxed">
                <p className="font-medium mb-0.5">Expected columns (case-insensitive):</p>
                <p>
                  <span className="font-mono">Item</span> / <span className="font-mono">Name</span> required ·{" "}
                  <span className="font-mono">Description</span>, <span className="font-mono">Unit Cost</span> /{" "}
                  <span className="font-mono">Price</span>, <span className="font-mono">Tax Rate</span>,{" "}
                  <span className="font-mono">SKU</span>, <span className="font-mono">Category</span> are optional.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <Button variant="ghost" size="sm" onClick={() => handleClose(false)} disabled={submitting}>Cancel</Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={!file || submitting}
                className={isDark ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90" : "bg-amber-500 text-white hover:bg-amber-600"}
                data-testid="items-import-submit"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                Import
              </Button>
            </div>
          </>
        )}

        {/* Result */}
        {result && !result.error && (
          <div className="space-y-3" data-testid="items-import-result">
            <div className={`rounded-lg p-3 flex items-start gap-2 border ${
              result.created > 0
                ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500"
                : "bg-amber-500/10 border-amber-500/20 text-amber-500"
            }`}>
              {result.created > 0
                ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-px" />
                : <AlertCircle className="w-4 h-4 shrink-0 mt-px" />}
              <div className="text-sm">
                <p className="font-semibold">
                  {result.created > 0 ? `Imported ${result.created} new item${result.created === 1 ? "" : "s"}` : "No new items"}
                </p>
                <p className="text-xs opacity-80 mt-0.5">
                  Out of {result.total_processed} row{result.total_processed === 1 ? "" : "s"} processed.
                </p>
              </div>
            </div>

            <div className="space-y-1 text-xs">
              {result.skipped_duplicate > 0 && (
                <div className="flex justify-between text-muted-foreground"><span>Skipped (already exists)</span><span data-testid="result-skipped-dup">{result.skipped_duplicate}</span></div>
              )}
              {result.skipped_empty > 0 && (
                <div className="flex justify-between text-muted-foreground"><span>Skipped (no name)</span><span>{result.skipped_empty}</span></div>
              )}
              {result.skipped_invalid > 0 && (
                <div className="flex justify-between text-red-400"><span>Skipped (invalid)</span><span>{result.skipped_invalid}</span></div>
              )}
              {result.detected_columns && (
                <div className="pt-2 border-t border-border/30 text-muted-foreground/80">
                  <p className="mb-0.5">Detected columns: {Object.keys(result.detected_columns).join(", ")}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <Button variant="ghost" size="sm" onClick={() => { setFile(null); setResult(null); }}>Import another</Button>
              <Button
                size="sm"
                onClick={() => handleClose(true)}
                className={isDark ? "bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90" : "bg-amber-500 text-white hover:bg-amber-600"}
                data-testid="items-import-done"
              >
                Done
              </Button>
            </div>
          </div>
        )}

        {result?.error && (
          <div className="space-y-3">
            <div className="rounded-lg p-3 flex items-start gap-2 border bg-red-500/10 border-red-500/20 text-red-500">
              <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              <div className="text-sm">
                <p className="font-semibold">Import failed</p>
                <p className="text-xs opacity-80 mt-0.5">{result.error}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button size="sm" variant="ghost" onClick={() => setResult(null)}>Try again</Button>
              <Button size="sm" onClick={() => handleClose(false)}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ItemsImportModal;
